import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Icon } from '../../../components/ui';
import { usePhone } from '../PhoneContext';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * IncomingCallCard — the ringing popup. Surfaces the resolved CRM
 * customer (name, company, active deal) the instant the phone rings,
 * so the rep knows who's calling before answering. Answering jumps to
 * the customer's card (handled in PhoneContext).
 */

const fmtIls = (n: number) => `₪${Math.round(n).toLocaleString('he-IL')}`;

export const IncomingCallCard: React.FC = () => {
  const { call, answer, hangup } = usePhone();
  const navigate = useNavigate();

  if (!call || call.direction !== 'inbound' || call.state !== 'ringing') return null;

  const cust = call.party?.customer;
  const deal = call.party?.deal;
  const name = cust?.fullName || 'מספר לא מזוהה';
  const company = cust?.company?.name;
  const { isAgent } = useAuth();
  const isUnknown = !cust;

  const createFromCaller = () => {
    // An agent cannot create or open a customer; the route would bounce back.
    if (isAgent) return;
    navigate(`/customers?tab=customers&createPhone=${encodeURIComponent(call.number)}`);
  };

  return (
    <div className="fixed bottom-4 end-4 z-[1300] w-[min(340px,calc(100vw-2rem))] animate-slide-up" role="alertdialog" aria-label="שיחה נכנסת">
      <div className="rounded-2xl bg-slate-900 border border-slate-700 text-slate-100 p-5 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] text-center">
        <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-amber-300 mb-4">
          <Icon name="call_received" size={15} /> שיחה נכנסת
        </div>

        <div className="relative w-fit mx-auto mb-3">
          <Avatar name={name} size="xl" />
          <span className="absolute -inset-1.5 rounded-3xl border-2 border-amber-400/70 animate-ping-slow pointer-events-none" />
        </div>

        <div className="font-display text-lg font-bold">{name}</div>
        {company && <div className="text-[13px] text-slate-400 mt-0.5">{company}</div>}
        <div className="text-[13px] text-slate-400 mt-0.5" dir="ltr">{call.number}</div>

        {deal && (
          <div className="inline-flex items-center gap-1.5 mt-3 bg-emerald-500/14 text-emerald-300 text-xs font-semibold px-3 py-1.5 rounded-full">
            <Icon name="handshake" size={15} />
            עסקה פעילה{deal.value ? ` · ${fmtIls(deal.value)}` : ''}
          </div>
        )}

        {isUnknown && (
          <button
            onClick={createFromCaller}
            className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded-full transition"
          >
            <Icon name="person_add" size={15} /> צור לקוח מהמספר
          </button>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={hangup}
            className="flex-1 h-[52px] rounded-2xl bg-red-500 hover:bg-red-600 text-white font-semibold flex items-center justify-center gap-2 transition"
          >
            <Icon name="call_end" size={22} /> דחה
          </button>
          <button
            onClick={answer}
            className="flex-1 h-[52px] rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 transition"
          >
            <Icon name="call" size={22} /> ענה
          </button>
        </div>
      </div>
    </div>
  );
};
